import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Tabs,
  Tab,
  Badge,
  Table,
  Modal,
  Form,
} from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import { useAuth } from "../Context/AuthContext";
import { getVendorsByOrganisation } from "../Api/CustomerMaster";
import {
  fetchCusVenCpByCusVenType,
  createCusVenCp,
} from "../Api/CusVenCpMasterApi";

const PURPLE = "#6f42c1";

/* ===== INFO TILE ===== */
const InfoItem = ({ label, value }) => (
  <Card className="border-0 shadow-sm h-100">
    <Card.Body>
      <small className="text-muted fw-semibold">{label}</small>
      <div className="fw-semibold mt-1">{value || "-"}</div>
    </Card.Body>
  </Card>
);

const ViewVendorDetails = () => {
  const { vendorCode } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [cpForm, setCpForm] = useState({
    contactPerName: "",
    contactPerNumber: "",
    contactPerEmail: "",
  });

  /* ===== LOAD VENDOR ===== */
  useEffect(() => {
    const loadVendor = async () => {
      try {
        const vendors = await getVendorsByOrganisation(user.organisationId);
        const selected = vendors.find((v) => v.vendorCode === vendorCode);
        setVendor(selected);
      } catch {
        toast.error("Failed to load vendor details");
      } finally {
        setLoading(false);
      }
    };

    loadVendor();
  }, [vendorCode, user]);

  /* ===== LOAD CONTACT PERSONS ===== */
  const loadMembers = async () => {
    try {
      setLoadingMembers(true);
      const data = await fetchCusVenCpByCusVenType(vendorCode);
      setMembers(data || []);
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    if (activeTab === "members") loadMembers();
  }, [activeTab]);

  /* ===== ADD CONTACT PERSON ===== */
  const handleSaveMember = async () => {
    try {
      await createCusVenCp(
        {
          cusVenTypeId: vendorCode,
          contactPerName: cpForm.contactPerName,
          contactPerNumber: cpForm.contactPerNumber,
          contactPerEmail: cpForm.contactPerEmail,
          orgId: String(user.organisationId),
        },
        user.userId
      );

      toast.success("Contact person added");
      setShowModal(false);
      setCpForm({
        contactPerName: "",
        contactPerNumber: "",
        contactPerEmail: "",
      });
      loadMembers();
    } catch {
      toast.error("Failed to add contact person");
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" style={{ color: PURPLE }} />
      </div>
    );
  }

  if (!vendor) {
    return <p className="text-center mt-4">Vendor not found</p>;
  }

  return (
    <div>
      {/* ===== HEADER ===== */}
      <div
        style={{
          background: "linear-gradient(135deg, #6f7ae0, #7b5fc4)",
          color: "#fff",
          padding: "28px 32px",
        }}
      >
        <div className="d-flex justify-content-between">
          <div>
            <h3 className="fw-bold">{vendor.name}</h3>
            <div className="opacity-75">Vendor Code: {vendor.vendorCode}</div>

            <div className="mt-3 d-flex gap-2 flex-wrap">
              <Badge bg="light" text="dark">
                {vendor.type}
              </Badge>
              <Badge bg={vendor.active === "Yes" ? "success" : "danger"}>
                {vendor.active}
              </Badge>
              <Badge bg="secondary">{vendor.currency}</Badge>
            </div>
          </div>

          <Button
            variant="light"
            onClick={() => navigate(-1)}
            style={{ height: "35px" }}
          >
            ← Back
          </Button>
        </div>
      </div>

      {/* ===== TABS ===== */}
      <div style={{ background: "#fff", borderBottom: "1px solid #eee" }}>
        <Container fluid>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            variant="underline"
            className="pt-2"
          >
            <Tab eventKey="overview" title="Overview" />
            <Tab eventKey="members" title="Member List" />
          </Tabs>
        </Container>
      </div>

      {/* ===== CONTENT ===== */}
      <Container fluid className="mt-4">
        {activeTab === "overview" && (
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <Row className="g-3">
                <Col md={4}>
                  <InfoItem label="Short Name" value={vendor.shortNames} />
                </Col>
                <Col md={4}>
                  <InfoItem label="GST No" value={vendor.gstno} />
                </Col>
                <Col md={4}>
                  <InfoItem label="PAN" value={vendor.pan} />
                </Col>

                <Col md={4}>
                  <InfoItem label="City" value={vendor.cityName} />
                </Col>
                <Col md={4}>
                  <InfoItem label="Country" value={vendor.countryCode} />
                </Col>
                <Col md={4}>
                  <InfoItem label="Mobile" value={vendor.mobileNumber} />
                </Col>

                <Col md={4}>
                  <InfoItem label="Entered By" value={vendor.enteredBy} />
                </Col>
                <Col md={4}>
                  <InfoItem
                    label="Entered Date"
                    value={moment(vendor.createdOnDate).format("LL")}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {activeTab === "members" && (
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between mb-3">
                <h6 className="fw-semibold">Contact Persons</h6>
                <Button
                  style={{ backgroundColor: PURPLE, border: "none" }}
                  onClick={() => setShowModal(true)}
                >
                  + Add Member
                </Button>
              </div>

              {loadingMembers ? (
                <Spinner animation="border" />
              ) : members.length > 0 ? (
                <Table hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Number</th>
                      <th>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m, i) => (
                      <tr key={m.cusVenId}>
                        <td>{i + 1}</td>
                        <td>{m.contactPerName}</td>
                        <td>{m.contactPerNumber}</td>
                        <td>{m.contactPerEmail}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted">No contact persons found.</p>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>

      {/* ===== MODAL ===== */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Contact Person</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={cpForm.contactPerName}
                onChange={(e) =>
                  setCpForm({ ...cpForm, contactPerName: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Number</Form.Label>
              <Form.Control
                value={cpForm.contactPerNumber}
                onChange={(e) =>
                  setCpForm({ ...cpForm, contactPerNumber: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control
                value={cpForm.contactPerEmail}
                onChange={(e) =>
                  setCpForm({ ...cpForm, contactPerEmail: e.target.value })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: PURPLE, border: "none" }}
            onClick={handleSaveMember}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ViewVendorDetails;
